import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText, ShieldCheck, ExternalLink } from "lucide-react";
import { useLiveResource } from "@/hooks/useLiveResource";

const GOLD = "#F5C542";
const ORANGE = "#F28C28";
const BG = "#120D0D";
const CARD = "#141414";

/**
 * Shared renderer for legal documents (Terms + Privacy).
 * Content is fetched from /api/public/legal/:slug and fully editable via admin.
 */
export default function LegalPage({ slug, defaultTitle, badge = "Documento Legal", icon: Icon = ScrollText, siblingSlug, siblingLabel }) {
  const { data: doc, loading } = useLiveResource(`/public/legal/${slug}`);
  const error = !loading && !doc;

  const [heading, accent] = splitTitle(doc?.title || defaultTitle);

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden"
      style={{
        background: `radial-gradient(1000px 700px at 50% -10%, ${GOLD}12, transparent 60%), radial-gradient(800px 500px at 90% 30%, ${ORANGE}12, transparent 60%), ${BG}`,
      }}
      data-testid={`${slug}-page`}
    >
      <header className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 pt-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar ao início
        </Link>
        <div className="flex items-center gap-2">
          <img src="/manga-symbol.png" alt="" className="w-8 h-8 object-contain" />
          <div className="font-display font-black text-sm tracking-tight">
            GRUPO <span style={{ color: GOLD }}>MANGA</span>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl overflow-hidden p-6 md:p-10 mb-6"
          style={{
            background: `linear-gradient(160deg, ${CARD}, #0a0a0a)`,
            border: `1px solid ${GOLD}30`,
            boxShadow: `0 0 60px ${GOLD}12`,
          }}
        >
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase"
            style={{ color: GOLD, background: `${GOLD}12`, border: `1px solid ${GOLD}55` }}
          >
            <Icon className="w-3 h-3" /> {badge}
          </div>
          <h1 className="mt-4 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl md:text-5xl leading-[0.95]">
            {heading}{" "}
            <span style={{ background: `linear-gradient(90deg, ${GOLD}, ${ORANGE})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              {accent}
            </span>
          </h1>
          <div className="mt-3 text-white/50 text-sm">
            {doc && (
              <>
                <div>{doc.company_name} — CNPJ {doc.cnpj}</div>
                {doc.updated_at_label && <div className="mt-1">Última atualização: {doc.updated_at_label}</div>}
              </>
            )}
          </div>
        </motion.div>

        {error && (
          <div className="rounded-2xl p-6 text-center text-white/50 text-sm border border-white/10">
            Não foi possível carregar o documento. Tente novamente mais tarde.
          </div>
        )}

        {!doc && !error && (
          <div className="rounded-2xl p-6 text-center text-white/40 text-sm border border-white/10">
            Carregando...
          </div>
        )}

        {doc && (
          <>
            {doc.intro && (
              <div className="rounded-2xl p-5 md:p-6 mb-6 text-white/75 text-sm md:text-[15px] leading-relaxed whitespace-pre-line"
                   style={{ background: `linear-gradient(160deg, ${CARD}, #0d0d0d)`, border: `1px solid ${GOLD}22` }}>
                {doc.intro}
              </div>
            )}
            <div className="space-y-6" data-testid={`${slug}-content`}>
              {(doc.sections || []).map((s, i) => (
                <motion.article
                  key={s.id || i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.3) }}
                  className="rounded-2xl p-5 md:p-6"
                  style={{
                    background: `linear-gradient(160deg, ${CARD}, #0d0d0d)`,
                    border: `1px solid ${GOLD}22`,
                  }}
                  id={`sec-${s.id || i}`}
                >
                  <h2 className="font-display font-bold text-white text-lg md:text-xl mb-3">{s.title}</h2>
                  <div className="space-y-3 text-white/70 text-sm md:text-[15px] leading-relaxed">
                    {(s.body || []).map((p, idx) => (
                      <p key={idx} className={/^([•\-]|[a-z]\)) /.test(p) ? "pl-2" : ""}>{p}</p>
                    ))}
                  </div>
                </motion.article>
              ))}
            </div>
          </>
        )}

        {siblingSlug && (
          <div className="mt-8 text-center">
            <Link
              to={`/${siblingSlug}`}
              className="inline-flex items-center gap-2 text-sm font-semibold underline decoration-dotted"
              style={{ color: GOLD }}
            >
              {siblingLabel} <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </section>

      <footer className="relative z-10 border-t border-white/[0.06] py-8 text-center text-white/40 text-xs">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="font-display font-bold text-white/70 mb-1 tracking-wide">© 2026 {doc?.company_name || "MANGA BET JOGOS E APOSTAS LTDA"}</div>
          <div>CNPJ: {doc?.cnpj || "57.496.990/0001-18"}</div>
          <div>Jogue com responsabilidade. Acesso permitido apenas para maiores de 18 anos.</div>
        </div>
      </footer>
    </div>
  );
}

function splitTitle(title) {
  const parts = (title || "").split(" ");
  if (parts.length < 2) return [title || "", ""];
  return [parts.slice(0, -1).join(" "), parts[parts.length - 1]];
}

export { ShieldCheck, ScrollText };
